import { object, string, boolean, number, AnyObjectSchema } from 'yup';
import 'yup-phone-lite';
import { checkProvacyNotice } from '../utils/default-privacy-notice';
import {
  checkPrivacyPolicy,
  isValidPrivacyPolicyUrl,
} from '../utils/default-privacy-policy';

const phoneRegExp = /^[+]?\d*([\s.-]?\d+)*$/;

// Validatori delle sezioni GDPR (informativa + privacy policy). Vengono
// applicati solo quando Blu Booking è attivo: in caso contrario (es. Layout
// Agenda web) gli accordion non vengono renderizzati e i campi non sono
// modificabili dall'utente, quindi non devono essere validati.
const privacyShape = {
  privacyNotice: string()
    .required(
      'Il campo informativa sul trattamento dei dati personali è obbligatorio'
    )
    .test(
      'Test compilazione',
      'Il campo informativa sul trattamento dei dati personali risulta non compilato',
      (value) => {
        if (!value || value === '') return false;
        return !checkProvacyNotice.test(value);
      }
    ),
  privacyPolicyMode: string(),
  // In modalità 'link' l'URL è in un campo dedicato: dev'essere valorizzato e
  // valido (http/https).
  privacyPolicyUrl: string().test(
    'Test url',
    "Inserisci l'URL della privacy policy",
    function (value) {
      if (this.parent.privacyPolicyMode !== 'link') return true;
      if (!value || value.trim() === '')
        return this.createError({
          message: "Inserisci l'URL della privacy policy",
        });
      return isValidPrivacyPolicyUrl(value)
        ? true
        : this.createError({
            message: 'Inserisci un URL valido (es. https://...)',
          });
    }
  ),
  privacyPolicy: string().test(
    'Test compilazione',
    'Il campo privacy policy è obbligatorio',
    function (value) {
      const mode = this.parent.privacyPolicyMode;
      // In modalità 'link' il contenuto è l'URL, validato su privacyPolicyUrl.
      if (mode === 'link') return true;

      const isEmpty = !value || value === '' || value === '<p></p>';

      // Modalità 'template': testo di default compilato e senza segnaposto.
      if (mode === 'template') {
        if (isEmpty)
          return this.createError({
            message: 'Compila il testo della privacy policy',
          });
        return checkPrivacyPolicy.test(value)
          ? this.createError({
              message:
                'Il testo della privacy policy contiene campi da compilare',
            })
          : true;
      }

      // Modalità 'paste' (o non impostata): il testo incollato deve esserci.
      return isEmpty
        ? this.createError({
            message: 'Inserisci il testo della privacy policy',
          })
        : true;
    }
  ),
};

// Quando Blu Booking non è attivo i campi privacy restano nello schema come
// stringhe libere (nessun vincolo), così non bloccano il salvataggio.
const privacyPassthrough = {
  privacyNotice: string(),
  privacyPolicyMode: string(),
  privacyPolicyUrl: string(),
  privacyPolicy: string(),
};

export const getLayoutSchema = (isBBActive = true): AnyObjectSchema =>
  object().shape({
    isTestingActive: boolean(),
    companyId: string(),
    name: string()
      .required('Il campo layout è obbligatorio')
      .min(1, 'Il campo layout è obbligatorio')
      .max(50, 'Il campo layout è troppo lungo'),
    email: string()
      .required('Il campo mail è obbligatorio')
      .min(1, 'Il campo mail è obbligatorio')
      .email('Mail non valida')
      .max(100, 'Il campo mail è troppo lungo'),
    telephone: string()
      .required('Il campo telefono è obbligatorio')
      .min(1, 'Il campo telefono è obbligatorio')
      //.phone("IT", "Telefono non valido")
      .matches(phoneRegExp, {
        message: 'Telefono non valido',
        excludeEmptyString: true,
      })
      .max(50, 'Il campo telefono è troppo lungo'),
    whatsappNumber: string()
      .nullable()
      .matches(phoneRegExp, {
        message: 'Numero whatsapp non valido',
        excludeEmptyString: true,
      })
      .max(50, 'Il numero di whatsapp è troppo lungo'),
    webSiteLink: string()
      .required('Il campo sito internet link è obbligatorio')
      .min(1, 'Il campo sito internet link è obbligatorio')
      .max(200, 'Il campo sito internet link è troppo lungo'),
    logo: string().required('Aggiungi un logo').min(1, 'Aggiungi un logo'),
    favicon: string()
      .required('Aggiungi una favicon')
      .min(1, 'Aggiungi una favicon'),
    address: string()
      .required('Il campo indirizzo è obbligatorio')
      .min(1, 'Il campo indirizzo è obbligatorio')
      .max(70, 'Il campo indirizzo è troppo lungo'),
    city: string()
      .required('Il campo città è obbligatorio')
      .min(1, 'Il campo città è obbligatorio')
      .max(60, 'Il campo città è troppo lungo'),
    region: string()
      .required('Il campo provincia è obbligatorio')
      .min(1, 'Il campo provincia è obbligatorio')
      .max(50, 'Il campo provincia è troppo lungo'),
    fax: string().max(20, 'Il campo fax è troppo lungo'),
    postalCode: string()
      .required('Il campo codice postale è obbligatorio')
      .min(1, 'Il campo codice postale è obbligatorio')
      .max(9, 'Il campo codice postale è troppo lungo'),
    vatCode: string()
      .required('Il campo partita iva è obbligatorio')
      .min(1, 'Il campo partita iva è obbligatorio')
      .max(150, 'Il campo partita iva è troppo lungo'),
    pec: string()
      .nullable()
      .email('Pec non valida')
      .max(100, 'Il campo pec è troppo lungo'),
    minBookingValue: number(),
    minBookingUnit: number(),
    maxBookingValue: number(),
    maxBookingUnit: number(),
    minCancellationValue: number(),
    minCancellationUnit: number(),
    timeSlot: number(),
    isRecallOn: boolean(),
    recallValue: number(),
    recallUnit: number(),
    isSmsActive: boolean(),
    maxRequestableAppointmentByDay: number(),
    isGoogleMapsActive: boolean(),
    isUserSelectable: boolean(),
    token: string(),
    appointmentProcess: number(),
    maxConcurrentAppointments: number(),
    ...(isBBActive ? privacyShape : privacyPassthrough),
  });

// Retrocompatibilità: schema di default (Blu Booking attivo).
export const schema = getLayoutSchema(true);
